import BUNDLED_MAPPING from '../../../public/hosts/mapping.json';

const HOST_MAPPING_STORAGE_KEY = 'hostTenantMapping';

// Cache do mapeamento em memória — pre-populado do sessionStorage para evitar
// round-trip de rede em recarregamentos de página
let cachedMapping = (() => {
  try {
    const stored = sessionStorage.getItem(HOST_MAPPING_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
})();
let mappingPromise = null;

// Mapeamento estático como fallback final — embutido no bundle a partir de
// public/hosts/mapping.json (fonte única), para não precisar de rede.
// Manter esse arquivo sincronizado com o mapping.json do S3 (a fonte de verdade).
const STATIC_FALLBACK_MAPPING = BUNDLED_MAPPING;

/**
 * Normaliza o hostname removendo pontos
 * @param {string} hostname - Hostname a ser normalizado
 * @returns {string} Hostname normalizado
 */
export const normalizeHostname = (hostname) => {
  return hostname.replace(/\./g, '').toLowerCase();
};

/**
 * Busca o mapeamento de hosts do S3 usando REACT_APP_S3_BASE_URL
 * @returns {Promise<Object|null>} Mapeamento de hosts ou null se não disponível
 */
const fetchMappingFromS3 = async () => {
  try {
    const s3BaseUrl = process.env.REACT_APP_S3_BASE_URL;
    
    if (!s3BaseUrl) {
      console.warn('REACT_APP_S3_BASE_URL não está configurada');
      return null;
    }
    
    const mappingUrl = `${s3BaseUrl}/mapping.json`;
    
    const response = await fetch(mappingUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });
    
    if (!response.ok) {
      // Se não encontrou o arquivo, retorna null para usar fallback
      return null;
    }
    
    const mapping = await response.json();
    
    if (mapping && typeof mapping === 'object') {
      return mapping;
    }
    
    return null;
  } catch (error) {
    // Erro ao buscar do S3 - usa fallback
    // Não loga erro para evitar poluição do console quando arquivo não existe
    return null;
  }
};

/**
 * Busca o mapeamento de hosts do arquivo JSON estático local
 * @returns {Promise<Object|null>} Mapeamento de hosts ou null se não disponível
 */
const fetchMappingFromFile = async () => {
  try {
    const response = await fetch('/hosts/mapping.json');
    
    if (!response.ok) {
      return null;
    }
    
    const mapping = await response.json();
    
    if (mapping && typeof mapping === 'object') {
      return mapping;
    }
    
    return null;
  } catch (error) {
    // Arquivo não existe ou erro ao ler - usa fallback estático
    return null;
  }
};

/**
 * Carrega o mapeamento de hosts de forma dinâmica
 * Tenta buscar do CMS primeiro, depois do arquivo JSON, e por último usa o fallback estático
 * @param {boolean} forceRefresh - Força recarregar mesmo se já estiver em cache
 * @returns {Promise<Object>} Mapeamento de hosts
 */
export const loadHostMapping = async (forceRefresh = false) => {
  // Se já está em cache e não é refresh forçado, retorna cache
  if (cachedMapping && !forceRefresh) {
    return cachedMapping;
  }
  
  // Se já está carregando, aguarda a promise existente
  if (mappingPromise && !forceRefresh) {
    return mappingPromise;
  }
  
  // Cria nova promise de carregamento
  mappingPromise = (async () => {
    try {
      // Tenta buscar do S3 primeiro (usando REACT_APP_S3_BASE_URL)
      let mapping = await fetchMappingFromS3();
      // Só o mapeamento do S3 é persistido no sessionStorage: se um fallback
      // (possivelmente desatualizado) fosse persistido, a aba ficaria presa nele
      // mesmo após "Tentar novamente"
      const isFromS3 = Boolean(mapping);

      // Se S3 não retornou, tenta arquivo JSON local
      if (!mapping) {
        mapping = await fetchMappingFromFile();
      }
      
      // Se nenhum dos dois funcionou, usa fallback estático
      if (!mapping) {
        mapping = STATIC_FALLBACK_MAPPING;
      }
      
      // Normaliza todas as chaves do mapeamento (remove pontos)
      const normalizedMapping = {};
      for (const [host, tenantId] of Object.entries(mapping)) {
        normalizedMapping[normalizeHostname(host)] = tenantId;
      }

      // Salva em cache
      cachedMapping = normalizedMapping;
      
      // Também salva no sessionStorage para persistência entre recarregamentos
      try {
        if (isFromS3) {
          sessionStorage.setItem(HOST_MAPPING_STORAGE_KEY, JSON.stringify(normalizedMapping));
        } else {
          sessionStorage.removeItem(HOST_MAPPING_STORAGE_KEY);
        }
      } catch (e) {
        // Ignora erros de storage (pode estar desabilitado)
      }
      
      return normalizedMapping;
    } catch (error) {
      console.error('Error loading host mapping:', error);
      // Em caso de erro, retorna fallback estático
      return STATIC_FALLBACK_MAPPING;
    } finally {
      mappingPromise = null;
    }
  })();
  
  return mappingPromise;
};

/**
 * Obtém o tenant ID para um hostname específico
 * @param {string} hostname - Hostname (será normalizado automaticamente)
 * @returns {Promise<string|null>} Tenant ID ou null se não encontrado
 */
export const getTenantIdFromHostname = async (hostname) => {
  const normalizedHost = normalizeHostname(hostname);
  const mapping = await loadHostMapping();
  if (mapping[normalizedHost]) {
    return mapping[normalizedHost];
  }

  // Host ausente no mapeamento em cache (pode estar desatualizado ou ter vindo
  // de um fallback) — força uma nova busca no S3 antes de desistir
  const freshMapping = await loadHostMapping(true);
  return freshMapping[normalizedHost] || null;
};

/**
 * Obtém o tenant ID para o hostname atual (síncrono, usa cache)
 * @returns {string|null} Tenant ID ou null se não encontrado
 */
export const getCurrentTenantId = () => {
  if (!cachedMapping) {
    // Tenta carregar do sessionStorage se disponível
    try {
      const stored = sessionStorage.getItem(HOST_MAPPING_STORAGE_KEY);
      if (stored) {
        cachedMapping = JSON.parse(stored);
      }
    } catch (e) {
      // Ignora erros
    }
    
    // Se ainda não tem cache, retorna null (deve chamar loadHostMapping primeiro)
    if (!cachedMapping) {
      return null;
    }
  }
  
  const hostname = window.location.hostname;
  const normalizedHost = normalizeHostname(hostname);
  return cachedMapping[normalizedHost] || null;
};

/**
 * Inicializa o mapeamento de hosts (deve ser chamado no início da aplicação)
 * @returns {Promise<Object>} Mapeamento de hosts carregado
 */
export const initializeHostMapping = async () => {
  return loadHostMapping();
};

export default {
  loadHostMapping,
  getTenantIdFromHostname,
  getCurrentTenantId,
  initializeHostMapping,
  normalizeHostname,
};

